import { storeOriginalTexts } from './split_for_pageoverlays'
import {
	getEasedStaggerDelayForTransitionOutAlt,
	getTextAnimationEasingForTarget,
	getTextAnimationStaggerDelay,
	getTextAnimationStaggerEasingForTarget,
	getMaskedTextRevealStartY,
	setLineTextMaskRevealClip,
} from '../util'
import { getWordLevelAnimationTargets, getCharacterLevelAnimationTargets } from '../text_animation_targets'
import {
	clearTypewriterBackgroundReveal,
	prepareTypewriterBackgroundReveal,
	revealTypewriterUnitBackground,
} from '../typewriter_background_reveal'
import {
	applyPreRevealVisualStateToTarget,
	discardTextAnimationTypewriterTimeouts,
	isTextAnimationSplitDomReadyForPlay,
	reapplyViewportAfterTextDomResplit,
	snapTextAnimationToFinishedAppearance,
	taResizeTrace,
	taResizeTraceTarget,
	taSplitLog,
	taSplitLogTarget,
} from '../text_animation_resplit_recovery'
import { getCurrentTargetsForPageOverlays, setCurrentTargetsForPageOverlays } from './targets'
import animation_lifecycle_controller from '../../animations/lifecycle/animation_lifecycle_controller'
import { getScrollContainerForElement } from '../../scroll_animations/viewport_progress'

// For a target inside a page overlay, return the IO `root` (the overlay's
// scroll container) and the visible rect of that container in viewport
// coords. Returns root=null + window-viewport rect when the target is on
// the normal page (window scrolls). Used so the rootMargin percentage and
// the "below / above viewport" tests measure against the OVERLAY'S visible
// area, not the full window — otherwise a centered modal-style overlay
// with space-around would trigger text animations relative to the window,
// firing too early at the top and re-marking elements as below before they
// visually leave the overlay's bottom edge.
const getIORootForTarget = (target) => {
	const pageOverlay = target && typeof target.closest === 'function' ? target.closest('.lay-overlay') : null
	if (pageOverlay) {
		return {
			root: pageOverlay,
			rect: pageOverlay.getBoundingClientRect(),
		}
	}
	const container = getScrollContainerForElement(target)
	if (!container || container === window) {
		return {
			root: null,
			rect: {
				top: 0,
				left: 0,
				right: window.innerWidth,
				bottom: window.innerHeight,
				width: window.innerWidth,
				height: window.innerHeight,
			},
		}
	}
	return {
		root: container,
		rect: container.getBoundingClientRect(),
	}
}
// Store animation timelines in a WeakMap
const animationTimelines = new WeakMap()
// Store timeout IDs for typewriter animations
const typewriterTimeouts = new WeakMap()

const isIOS = () => {
	return typeof document !== 'undefined' && document.documentElement.classList.contains('ios')
}

const getDebugLabelForTarget = (target) => {
	if (!target) {
		return '[po-ta] <missing-target>'
	}
	const text = (target.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 60)
	const animationType = target.getAttribute('data-animation-type') || 'unknown'
	return `[po-ta] ${animationType} "${text}"`
}

const isDebugEnabled = () => {
	return !!(
		typeof window !== 'undefined' &&
		(window.layTextAnimationDebug === true ||
			(window.location && window.location.search.indexOf('layTextAnimationDebug=1') !== -1))
	)
}

const debugTa = (target, message, extra) => {
	if (!isDebugEnabled()) {
		return
	}
	const label = getDebugLabelForTarget(target)
	if (extra !== undefined) {
		console.log(label, message, JSON.stringify(extra))
	} else {
		console.log(label, message)
	}
}

// Snapshot the visual state of the target so we can diagnose flickers.
// Combines computed visibility/opacity, inline opacity, the
// `lay-text-animation-initialized` class, and the first child's transform —
// enough to tell whether the text is currently painted to the screen.
const snapshotVisualState = (target, label) => {
	if (!isDebugEnabled()) {
		return
	}
	if (!target) return
	const cs = window.getComputedStyle(target)
	const firstChild = target.querySelector('.lay-text-line, .char') || target.children[0]
	const childCs = firstChild ? window.getComputedStyle(firstChild) : null
	console.log('[po-ta-snapshot]', label, getDebugLabelForTarget(target), {
		t: performance.now().toFixed(1),
		visibility: cs.visibility,
		opacity: cs.opacity,
		inlineOpacity: target.style.opacity,
		hasInitClass: target.classList.contains('lay-text-animation-initialized'),
		hasInViewClass: target.classList.contains('text-animation-in-view'),
		hasNeverPlayedClass: target.classList.contains('text-animation-never-played'),
		childOpacity: childCs ? childCs.opacity : null,
		childTransform: childCs ? childCs.transform : null,
	})
}

// Driven by `scrub_for_pageoverlays.js` (manual timeline.seek per scroll
// tick) — IO-mode bookkeeping in this file must skip these.
const isScrubTarget = (target) => {
	return !!(target && target.getAttribute && target.getAttribute('data-animation-scrub') === 'true')
}

const resumeInViewTextAnimationsIfNeeded = (targets) => {
	if (!isIOS()) {
		return
	}
	targets.forEach((target) => {
		if (!target || !target.classList.contains('text-animation-in-view')) {
			return
		}
		const timeline = animationTimelines.get(target)
		if (!timeline || timeline.completed) {
			return
		}
		const progress = typeof timeline.progress === 'number' ? timeline.progress : 0
		if (progress <= 0 || progress >= 100) {
			return
		}
		if (timeline.paused) {
			debugTa(target, 'resume after iOS scroll/touch fallback', { progress })
			timeline.play()
		}
	})
}

// Configuration for text splitting
let selector = '.lay-text-animation'
let column_selectors = ''
const pageOverlayOpeningGuardStyleId = 'lay-page-overlay-text-animation-opening-guard'

const ensurePageOverlayOpeningGuardStyle = () => {
	if (document.getElementById(pageOverlayOpeningGuardStyleId)) {
		return
	}
	const style = document.createElement('style')
	style.id = pageOverlayOpeningGuardStyleId
	style.textContent = '.lay-overlay.lay-text-animations-opening .lay-text-animation{visibility:hidden!important;}'
	document.head.appendChild(style)
}

const addPageOverlayOpeningGuard = (root) => {
	ensurePageOverlayOpeningGuardStyle()
	root.classList.add('lay-text-animations-opening')
	if (root._layTextAnimationOpeningGuardTimeout) {
		clearTimeout(root._layTextAnimationOpeningGuardTimeout)
	}
	root._layTextAnimationOpeningGuardTimeout = setTimeout(() => {
		root.classList.remove('lay-text-animations-opening')
		root._layTextAnimationOpeningGuardTimeout = null
	}, 4000)
}

const removePageOverlayOpeningGuard = (target) => {
	const root = target && target.closest ? target.closest('.lay-overlay') : null
	if (!root) return
	if (root._layTextAnimationOpeningGuardTimeout) {
		clearTimeout(root._layTextAnimationOpeningGuardTimeout)
		root._layTextAnimationOpeningGuardTimeout = null
	}
	root.classList.remove('lay-text-animations-opening')
}

const removePageOverlayOpeningGuardAfterAnimationStart = (target) => {
	const release = () => removePageOverlayOpeningGuard(target)
	if (typeof requestAnimationFrame !== 'function') {
		setTimeout(release, 75)
		return
	}
	requestAnimationFrame(() => {
		requestAnimationFrame(() => {
			setTimeout(release, 50)
		})
	})
}

const resetPageOverlayTextAnimationsForOpening = ($target) => {
	const root = $target && $target[0] ? $target[0] : null
	if (!root) return
	addPageOverlayOpeningGuard(root)

	if (window.currentPoTaObservers) {
		window.currentPoTaObservers()
		window.currentPoTaObservers = null
	}

	const targets = Array.from(root.querySelectorAll(selector))
	setCurrentTargetsForPageOverlays(targets)
	targets.forEach((target) => {
		if (!target || isScrubTarget(target)) return
		const oldTimeline = animationTimelines.get(target)
		if (oldTimeline) {
			oldTimeline.pause()
			animationTimelines.delete(target)
		}
		discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)
		target._layPendingPlayAfterSplit = false
		target.classList.remove('lay-text-animation-initialized')
		target.classList.remove('text-animation-in-view')
		target.classList.remove('text-animation-ready-to-play')
		target.classList.add('text-animation-never-played')
		applyPreRevealVisualStateToTarget(target)
	})
}

window.laytheme.on('page_overlay_before_shown', resetPageOverlayTextAnimationsForOpening)
window.laytheme.on('overlayhidden', resetPageOverlayTextAnimationsForOpening)
window.laytheme.on('page_overlay_close_end', resetPageOverlayTextAnimationsForOpening)

window.laytheme.on('texts_are_split_for_page_overlays', function (detail) {
	let targets = getCurrentTargetsForPageOverlays()
	const fromResize = !!(detail && detail.fromResize)
	const resizeTargets = detail && detail.resizeMutationTargets

	taSplitLog('texts_are_split_for_page_overlays handler', {
		targetCount: targets.length,
		fromResize,
		resizeMutationTargetCount: resizeTargets ? resizeTargets.size : 0,
	})
	taResizeTrace('[overlay] texts_are_split handler enter', {
		targetCount: targets.length,
		fromResize,
		resizeMutationTargetCount: resizeTargets ? resizeTargets.size : 0,
	})

	targets.forEach((target) => {
		// Scrub targets are driven by scrub_for_pageoverlays.js — skip the
		// IO-mode bookkeeping here.
		if (isScrubTarget(target)) {
			return
		}
		const inResizeMutationSet = !!(resizeTargets && resizeTargets.has(target))
		const fromResizeForTarget =
			inResizeMutationSet || (!resizeTargets || resizeTargets.size === 0 ? fromResize : false)

		const oldTimeline = animationTimelines.get(target)

		taResizeTraceTarget(target, '[overlay] texts_are_split target', {
			inResizeMutationSet,
			fromResizeForTarget,
			globalFromResize: fromResize,
			hadTimeline: !!oldTimeline,
			timelineProgress: oldTimeline && typeof oldTimeline.progress === 'number' ? oldTimeline.progress : null,
			timelineCompleted: oldTimeline ? !!oldTimeline.completed : null,
		})

		if (fromResizeForTarget) {
			if (oldTimeline) {
				debugTa(target, 'texts_are_split cleanup old timeline', {
					progress: oldTimeline.progress,
					completed: oldTimeline.completed,
					paused: oldTimeline.paused,
				})
				oldTimeline.pause()
				animationTimelines.delete(target)
				discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)
			}
			target._layPendingPlayAfterSplit = false
			const wasPlayedBefore =
				!!oldTimeline ||
				target.classList.contains('lay-text-animation-initialized') ||
				target.classList.contains('text-animation-in-view')
			if (wasPlayedBefore) {
				taResizeTraceTarget(target, '[overlay] texts_are_split → resize: SNAP finished', {
					hadTimeline: !!oldTimeline,
					initialized: target.classList.contains('lay-text-animation-initialized'),
				})
				taSplitLogTarget(target, '[overlay] texts_are_split: resize — snap finished', {
					hadTimeline: !!oldTimeline,
					initialized: target.classList.contains('lay-text-animation-initialized'),
				})
				snapTextAnimationToFinishedAppearance(target)
				target.classList.remove('text-animation-never-played')
				target.classList.remove('text-animation-ready-to-play')
				target.classList.add('text-animation-in-view')
				debugTa(target, 'texts_are_split: resize resplit — final state only (no replay)')
			} else {
				taResizeTraceTarget(target, '[overlay] texts_are_split → resize: skip snap (never played)', {})
				taSplitLogTarget(target, '[overlay] texts_are_split: resize — skip snap (never played yet)')
			}
			return
		}

		if (!oldTimeline) {
			taResizeTraceTarget(target, '[overlay] texts_are_split → non-resize: skip (no timeline)', {
				inResizeMutationSet,
				fromResizeForTarget,
			})
			taSplitLogTarget(target, '[overlay] texts_are_split: skip — no timeline')
			return
		}

		debugTa(target, 'texts_are_split cleanup old timeline', {
			progress: oldTimeline.progress,
			completed: oldTimeline.completed,
			paused: oldTimeline.paused,
		})
		oldTimeline.pause()
		animationTimelines.delete(target)
		discardTextAnimationTypewriterTimeouts(typewriterTimeouts, target)

		const progress = typeof oldTimeline.progress === 'number' ? oldTimeline.progress : 0
		const wasCompleted = !!(oldTimeline.completed || progress >= 99.5)

		taSplitLogTarget(target, '[overlay] texts_are_split: had timeline', {
			progress,
			completed: oldTimeline.completed,
			wasCompleted,
			fromResize: fromResizeForTarget,
		})

		if (wasCompleted) {
			taResizeTraceTarget(target, '[overlay] texts_are_split → non-resize: SNAP (was completed)', { progress })
			snapTextAnimationToFinishedAppearance(target)
			target.classList.remove('text-animation-never-played')
			target.classList.remove('text-animation-ready-to-play')
			target.classList.add('text-animation-in-view')
			debugTa(target, 'texts_are_split: relayout after finished animation, snapped to final state')
		} else {
			taResizeTraceTarget(target, '[overlay] texts_are_split → non-resize: REPLAY (in-progress)', {
				progress,
				completed: oldTimeline.completed,
			})
			taSplitLogTarget(target, '[overlay] texts_are_split: in-progress → reset + reapplyViewport')
			resetAnimationState(target)
			reapplyViewportAfterTextDomResplit(target, playAnimation, debugTa)
		}
	})
})

// Function to set up intersection observer for all text elements
const initObserver = () => {
	taSplitLog('[overlay] initObserver start', { targetCount: getCurrentTargetsForPageOverlays().length })
	// Create a map to store observers for each target
	const observers = new Map()
	const resetObservers = new Map()
	let resumeTimeoutId = null
	const scheduleIOSResumeCheck = () => {
		if (!isIOS()) {
			return
		}
		if (resumeTimeoutId) {
			window.clearTimeout(resumeTimeoutId)
		}
		resumeTimeoutId = window.setTimeout(() => {
			resumeInViewTextAnimationsIfNeeded(getCurrentTargetsForPageOverlays())
		}, 80)
	}

	// Set up individual intersection observer for each target
	getCurrentTargetsForPageOverlays().forEach((target) => {
		// Scrub targets are driven by scrub_for_pageoverlays.js, not by IO.
		if (isScrubTarget(target)) {
			return
		}
		// Get the data-top value or use default 80%
		const dataTop = parseFloat(target.getAttribute('data-top')) || 80
		let top = 100 - parseInt(dataTop)
		// Create negative rootMargin to trigger when viewport is dataTop% away from element
		const rootMargin = `0px 0px -${top}% 0px`

		// Resolve IO root (= overlay scroll container). rootRect is the
		// container's visible bbox in viewport coords; we compare element
		// rects against it instead of window-viewport so a centered modal
		// overlay's trigger / hide lines actually line up with what the
		// user sees.
		const { root: ioRoot, rect: rootRect } = getIORootForTarget(target)

		// Check initial position
		const rect = target.getBoundingClientRect()
		const wasAlreadyRevealed =
			target.classList.contains('text-animation-in-view') ||
			target.classList.contains('lay-text-animation-initialized')
		if (wasAlreadyRevealed && rect.top <= rootRect.bottom) {
			snapTextAnimationToFinishedAppearance(target)
			target.classList.remove('text-animation-never-played')
			target.classList.remove('text-animation-ready-to-play')
			if (rect.top >= rootRect.top) {
				target.classList.add('text-animation-in-view')
			} else {
				target.classList.remove('text-animation-in-view')
			}
		} else {
			// Mark as never played initially
			target.classList.add('text-animation-never-played')
		}

		if (rect.top > rootRect.bottom) {
			// Element is below viewport - mark as ready to animate when scrolling down
			target.classList.add('text-animation-ready-to-play')
			debugTa(target, 'init: below viewport, marked ready-to-play', {
				rectTop: rect.top,
				rootBottom: rootRect.bottom,
			})
		} else if (rect.top >= rootRect.top && !wasAlreadyRevealed) {
			debugTa(target, 'init: in viewport, playing immediately', {
				rectTop: rect.top,
				rootBottom: rootRect.bottom,
			})
			if (playAnimation(target)) {
				target.classList.remove('text-animation-never-played')
				target.classList.add('text-animation-in-view')
			}
		} else {
			debugTa(target, 'init: above viewport, waiting without ready flag', {
				rectTop: rect.top,
				rectBottom: rect.bottom,
			})
		}
		// If element is above viewport (else case), don't add any ready class

		// Observer for triggering animations
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						const timeline = animationTimelines.get(entry.target)
						const progress = timeline && typeof timeline.progress === 'number' ? timeline.progress : 0
						if (timeline && timeline.paused && !timeline.completed && progress > 0 && progress < 100) {
							debugTa(entry.target, 'observer: intersecting, resume paused in-progress timeline', {
								progress,
								readyToPlay: entry.target.classList.contains('text-animation-ready-to-play'),
								neverPlayed: entry.target.classList.contains('text-animation-never-played'),
							})
							timeline.play()
							entry.target.classList.add('text-animation-in-view')
							return
						}
						// Check if scrolling from below (down scroll) or never played before
						const rootTop = entry.rootBounds ? entry.rootBounds.top : 0
						const comingFromBelow = entry.boundingClientRect.top > rootTop
						const neverPlayed = entry.target.classList.contains('text-animation-never-played')
						const readyToPlay = entry.target.classList.contains('text-animation-ready-to-play')
						const readyFrom = entry.target.getAttribute('data-text-animation-ready-from') || 'from_below'
						const isAllowedReEntry = readyFrom === 'both' || readyFrom === (comingFromBelow ? 'from_below' : 'from_above')

						// Play if: (coming from below AND ready) OR never played before
						if ((readyToPlay && isAllowedReEntry) || neverPlayed) {
							debugTa(entry.target, 'observer: intersecting, calling playAnimation', {
								comingFromBelow,
								neverPlayed,
								readyToPlay,
								top: entry.boundingClientRect.top,
							})
							if (playAnimation(entry.target)) {
								entry.target.classList.remove('text-animation-ready-to-play')
								entry.target.removeAttribute('data-text-animation-ready-from')
								entry.target.classList.remove('text-animation-never-played')
								entry.target.classList.add('text-animation-in-view')
							}
						} else {
							debugTa(entry.target, 'observer: intersecting, but NOT playing', {
								comingFromBelow,
								neverPlayed,
								readyToPlay,
								top: entry.boundingClientRect.top,
							})
						}
					}
				})
			},
			{
				threshold: 0,
				rootMargin: rootMargin,
				root: ioRoot,
			}
		)

		// Observer for detecting when element is completely out of view
		const resetObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) {
						// rootBounds is the IO root's bbox in viewport coords (window
						// viewport when root=null). Comparing element rect against
						// it correctly identifies above/below relative to the
						// overlay's visible area for modal-style page overlays.
						const rb = entry.rootBounds
						const rootBottom = rb ? rb.bottom : window.innerHeight
						const rootTop = rb ? rb.top : 0
						const isBelow = entry.boundingClientRect.top > rootBottom
						const isAbove = entry.boundingClientRect.bottom < rootTop
						const reEntryMode = normalizeReEntryDirection(entry.target.getAttribute('data-repeat-on-re-enter-direction'))

						if (isBelow) {
							const timeline = animationTimelines.get(entry.target)
							const inProgress = timeline && !timeline.completed && timeline.progress > 0
							if (isIOS() && inProgress) {
								debugTa(entry.target, 'resetObserver: below viewport, skip reset on iOS because animation in progress', {
									progress: timeline.progress,
								})
								return
							}
							if (entry.target.getAttribute('data-repeat-on-re-enter') === '0' || !reEntryAllows(reEntryMode, 'from_below')) {
								return
							}
							// Element went below viewport - reset and mark as ready to play again
							debugTa(entry.target, 'resetObserver: below viewport, reset animation state', {
								top: entry.boundingClientRect.top,
								rootBottom,
							})
							resetAnimationState(entry.target)
							entry.target.classList.add('text-animation-ready-to-play')
							entry.target.setAttribute('data-text-animation-ready-from', 'from_below')
							entry.target.classList.remove('text-animation-in-view')
						} else if (isAbove) {
							if (entry.target.getAttribute('data-repeat-on-re-enter') !== '0' && reEntryAllows(reEntryMode, 'from_above')) {
								debugTa(entry.target, 'resetObserver: above viewport, reset animation state')
								resetAnimationState(entry.target)
								entry.target.classList.add('text-animation-ready-to-play')
								entry.target.setAttribute('data-text-animation-ready-from', 'from_above')
								entry.target.classList.remove('text-animation-in-view')
								return
							}
							// Element went above viewport - just remove classes, DON'T reset visual state
							// Leave text visible in its finished state
							debugTa(entry.target, 'resetObserver: above viewport, leaving finished state')
							entry.target.classList.remove('text-animation-ready-to-play')
							entry.target.classList.remove('text-animation-in-view')
						}
					}
				})
			},
			{
				threshold: 0,
				rootMargin: '0px',
				root: ioRoot,
			}
		)

		// Store observers and start observing
		observers.set(target, observer)
		resetObservers.set(target, resetObserver)
		observer.observe(target)
		resetObserver.observe(target)
	})

	window.addEventListener('scroll', scheduleIOSResumeCheck, { passive: true })
	window.addEventListener('touchend', scheduleIOSResumeCheck, { passive: true })
	window.addEventListener('touchcancel', scheduleIOSResumeCheck, { passive: true })

	// Return cleanup function
	return () => {
		if (resumeTimeoutId) {
			window.clearTimeout(resumeTimeoutId)
		}
		window.removeEventListener('scroll', scheduleIOSResumeCheck)
		window.removeEventListener('touchend', scheduleIOSResumeCheck)
		window.removeEventListener('touchcancel', scheduleIOSResumeCheck)
		observers.forEach((observer) => observer.disconnect())
		resetObservers.forEach((observer) => observer.disconnect())
		observers.clear()
		resetObservers.clear()
	}
}

const normalizeReEntryDirection = (value) => {
	return value === 'from_above' || value === 'both' || value === 'from_below' ? value : 'from_below'
}

const reEntryAllows = (mode, side) => {
	return mode === side || mode === 'both'
}

// Function to reset animation state
const resetAnimationState = (target) => {
	// Get the timeline if it exists
	const timeline = animationTimelines.get(target)
	if (timeline) {
		debugTa(target, 'resetAnimationState: pause + seek(0)', {
			progress: timeline.progress,
			completed: timeline.completed,
		})
		// Reset the timeline to its initial state
		timeline.pause()
		timeline.seek(0)
	}

	const animationType = target.getAttribute('data-animation-type')
	if (animationType === 'typewriter' || animationType === 'typewriter_words' || animationType === 'typewriter_lines') {
		const existingTimeouts = typewriterTimeouts.get(target)
		if (existingTimeouts) {
			existingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
			typewriterTimeouts.delete(target)
		}
		clearTypewriterBackgroundReveal(target)
	}

	applyPreRevealVisualStateToTarget(target)
	taSplitLogTarget(target, '[overlay] resetAnimationState done (via applyPreReveal)')
}

function computeBuildAnimScaling(target, itemCount, userDuration, userStagger) {
	const targetTotal = parseInt(target.getAttribute('data-build-anim-total-duration'), 10)
	if (!isFinite(targetTotal) || targetTotal <= 0) return null
	const N = Math.max(1, itemCount)
	const expected = Math.max(1, (N - 1) * userStagger + userDuration)
	const scale = Math.max(0.05, Math.min(10, targetTotal / expected))
	return {
		targetTotal: targetTotal,
		duration: userDuration * scale,
		stagger: userStagger * scale,
	}
}

function buildScaledDelayFn(baseDelay, target, totalDurationAttr, scale) {
	if (!scale) {
		return function (el, index, length) {
			return baseDelay + getEasedStaggerDelayForTransitionOutAlt(index, length, target, totalDurationAttr)
		}
	}
	const staggerEasing = getTextAnimationStaggerEasingForTarget(target)
	return function (el, index, length) {
		return baseDelay + getTextAnimationStaggerDelay(index, length, scale.stagger, staggerEasing)
	}
}

// Function to create and store animation timeline
const createAnimationTimeline = (target) => {
	// const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	// const dataDuration = parseFloat(target.getAttribute('data-duration')) || 500

	// Calculate base duration based on number of lines

	const animationType = target.getAttribute('data-animation-type')
	const delay = parseInt(target.getAttribute('data-delay'), 10) || 0
	const totalDuration = parseInt(target.getAttribute('data-duration'), 10) || 40
	const directionAttrRaw = target.getAttribute('data-direction')
	const direction = (directionAttrRaw === 'left' || directionAttrRaw === 'right' || directionAttrRaw === 'up' || directionAttrRaw === 'down' || directionAttrRaw === 'bottomLeft' || directionAttrRaw === 'bottomRight') ? directionAttrRaw : null
	const readingDirectionAttrRaw = target.getAttribute('data-reading-direction')
	const readingDirection = (readingDirectionAttrRaw === 'ltr' || readingDirectionAttrRaw === 'rtl') ? readingDirectionAttrRaw : null
	const slideAmountAttrRaw = parseFloat(target.getAttribute('data-slide-amount'))
	const slideAmount = isFinite(slideAmountAttrRaw) && slideAmountAttrRaw >= 0 ? slideAmountAttrRaw : 0.5
	const slideStart = (axis) => axis === 0 ? '0em' : (axis * slideAmount) + 'em'
	const scaleFromAttrRaw = parseFloat(target.getAttribute('data-scale-from'))
	const scaleFromActive = isFinite(scaleFromAttrRaw) && scaleFromAttrRaw !== 100
	const scaleFrom = scaleFromActive ? scaleFromAttrRaw / 100 : null
	const blurFromAttrRaw = parseFloat(target.getAttribute('data-blur-from-px'))
	const blurActive = isFinite(blurFromAttrRaw) && blurFromAttrRaw > 0
	const blurFilterFrom = blurActive ? 'blur(' + blurFromAttrRaw + 'px)' : null
	const directionForType = (t) => {
		if (direction) return direction
		if (t === 'fade_words' || t === 'fade_characters' || t === 'typewriter_words' || t === 'typewriter' || t === 'typewriter_fade') return 'left'
		return 'up'
	}
	const slideVector = (t) => {
		switch (directionForType(t)) {
			case 'left': return { dx: -1, dy: 0 }
			case 'right': return { dx: 1, dy: 0 }
			case 'up': return { dx: 0, dy: 1 }
			case 'down': return { dx: 0, dy: -1 }
			case 'bottomLeft': return { dx: -1, dy: 1 }
			case 'bottomRight': return { dx: 1, dy: 1 }
			default: return { dx: 0, dy: 1 }
		}
	}
	const shouldReverseStaggerForType = (t) => {
		if (readingDirection !== null) {
			return readingDirection === 'rtl'
		}
		if (t === 'typewriter_lines') {
			return directionForType(t) === 'down'
		}
		if (t === 'fade_words' || t === 'fade_characters' || t === 'typewriter_words' || t === 'typewriter' || t === 'typewriter_fade') {
			return directionForType(t) === 'right'
		}
		return false
	}
	const wrapDelayForDirection = (delayFn, t) => {
		if (!shouldReverseStaggerForType(t)) return delayFn
		return function (el, index, length) {
			return delayFn(el, length - 1 - index, length)
		}
	}
	const maskedTranslateYStart = (t) => {
		if (directionForType(t) !== 'down') return getMaskedTextRevealStartY
		return function (el) { return -getMaskedTextRevealStartY(el) }
	}
	const staggerAttrRaw = parseInt(target.getAttribute('data-stagger'), 10)
	const hasExplicitStagger = isFinite(staggerAttrRaw) && staggerAttrRaw >= 0
	const perItemDuration = hasExplicitStagger ? totalDuration : undefined
	const userDurationForFit = hasExplicitStagger ? totalDuration : 1000
	const userStaggerForFit = hasExplicitStagger && staggerAttrRaw > 0 ? staggerAttrRaw : totalDuration
	const easing = getTextAnimationEasingForTarget(target)
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	let timeline = anime.timeline({
		easing: easing,
		autoplay: false, // Don't start playing automatically
		begin: function () {
			debugTa(target, 'timeline begin', {
				easing,
				delay,
				duration: totalDuration,
			})
		},
		complete: function () {
			debugTa(target, 'timeline complete')
			if (
				animationType === 'masked_lines' ||
				animationType === 'masked_words' ||
				animationType === 'masked_characters'
			) {
				setLineTextMaskRevealClip(lineMasks, false)
			}
			try {
				target.dispatchEvent(new CustomEvent('lay-text-anim-trigger-play-complete', { bubbles: false }))
			} catch (_) {}
		},
	})

	switch (animationType) {
		case 'masked_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: lines,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'masked_words': {
			const characters = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'masked_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			debugTa(target, 'create timeline: masked_characters targets', {
				characterTargetCount: characters.length,
				charSpanCount: target.querySelectorAll('.lay-text-line .char').length,
				lineMaskCount: lineMasks.length,
			})

			const cfg = {
				targets: characters,
				translateY: [maskedTranslateYStart(animationType), 0],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)

			break
		}
		case 'fade_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: lines,
				opacity: [0, 1],
				delay: buildScaledDelayFn(delay, target, totalDuration, scale),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_words': {
			const characters = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'typewriter_lines':
		case 'typewriter_words':
		case 'typewriter': {
			// Mixed list: laybuttons collapse to a single step (their bg/border
			// can't sensibly type in per-char). See grid variant for full notes.
			const units = animationType === 'typewriter_lines'
				? Array.from(target.querySelectorAll('.lay-text-line'))
				: animationType === 'typewriter_words'
					? getWordLevelAnimationTargets(target)
					: getCharacterLevelAnimationTargets(target)
			let perCharGap = hasExplicitStagger ? staggerAttrRaw : totalDuration
			const _twScale = computeBuildAnimScaling(target, units.length, 0, perCharGap)
			if (_twScale) perCharGap = _twScale.stagger
			const baseDelay = perCharGap
			const staggerEasing = getTextAnimationStaggerEasingForTarget(target)

			// first set all characters/buttons to opacity 0
			// this is needed when the text is visible on load
			anime.set(units, {
				opacity: 0,
			})

			// `targets: []` made anime.js skip `begin` on initial paint (text
			// visible on load stayed at opacity 0 forever). Pin to the real
			// target with a no-op opacity tween so anime.js has something to
			// schedule and `begin` fires reliably. Same fix as grid variant.
			const typewriterTotalMs = Math.max(1, delay + units.length * baseDelay)
			const currentOpacity = target.style.opacity !== '' ? target.style.opacity : '1'
			timeline.add({
				targets: target,
				opacity: [currentOpacity, currentOpacity],
				duration: typewriterTotalMs,
				begin: function (anim) {
					// Clear any existing timeouts for this target
					const existingTimeouts = typewriterTimeouts.get(target)
					if (existingTimeouts) {
						existingTimeouts.forEach((timeoutId) => clearTimeout(timeoutId))
					}
					clearTypewriterBackgroundReveal(target)

					// first set all characters/buttons to opacity 0
					// this is needed when the text is visible on load
					anime.set(units, {
						opacity: 0,
					})

					// Store timeout IDs for cleanup
					const timeoutIds = []

					// Custom typewriter implementation without transitions
					const reverseTw = shouldReverseStaggerForType(animationType)
					const twLen = units.length
					prepareTypewriterBackgroundReveal(target, units, reverseTw)
					units.forEach((char, index) => {
						const effIdx = reverseTw ? twLen - 1 - index : index
						const timeoutId = setTimeout(() => {
							char.style.opacity = '1'
							revealTypewriterUnitBackground(target, char)
						}, delay + getTextAnimationStaggerDelay(effIdx, twLen, baseDelay, staggerEasing))
						timeoutIds.push(timeoutId)
					})

					// Store timeout IDs for cleanup
					typewriterTimeouts.set(target, timeoutIds)
				},
			})
			break
		}
		case 'typewriter_fade': {
			const characters = getCharacterLevelAnimationTargets(target)
			let perCharGap = hasExplicitStagger ? staggerAttrRaw : totalDuration
			let perCharDuration = hasExplicitStagger && totalDuration > 0 ? totalDuration : 1000
			const _twfScale = computeBuildAnimScaling(target, characters.length, perCharDuration, perCharGap)
			if (_twfScale) {
				perCharGap = _twfScale.stagger
				perCharDuration = _twfScale.duration
			}
			const reverseTwf = shouldReverseStaggerForType('typewriter_fade')
			const staggerEasing = getTextAnimationStaggerEasingForTarget(target)

			const cfg = {
				targets: characters,
				opacity: [0, 1],
				duration: perCharDuration,
				delay: function (el, index, length) {
					const effIdx = reverseTwf ? length - 1 - index : index
					return delay + getTextAnimationStaggerDelay(effIdx, length, perCharGap, staggerEasing)
				},
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		case 'fade_whole_text': {
			const _fwtScale = computeBuildAnimScaling(target, 1, 1000, 0)
			const durMs = parseInt(target.getAttribute('data-duration'), 10)
			const durationMs = _fwtScale
				? _fwtScale.targetTotal
				: (isFinite(durMs) && durMs > 0 ? durMs : 1000)
			const cfg = {
				targets: target,
				opacity: [0, 1],
				duration: durationMs,
				delay: delay,
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_whole_text': {
			const _fwsScale = computeBuildAnimScaling(target, 1, 1000, 0)
			const durMs = parseInt(target.getAttribute('data-duration'), 10)
			const durationMs = _fwsScale
				? _fwsScale.targetTotal
				: (isFinite(durMs) && durMs > 0 ? durMs : 1000)
			const vec = slideVector(animationType)
			const cfg = {
				targets: target,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				duration: durationMs,
				delay: delay,
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_lines': {
			const lines = target.querySelectorAll('.lay-text-line')
			const scale = computeBuildAnimScaling(target, lines.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: lines,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_words': {
			const words = getWordLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, words.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: words,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
		case 'fade_and_slide_characters': {
			const characters = getCharacterLevelAnimationTargets(target)
			const scale = computeBuildAnimScaling(target, characters.length, userDurationForFit, userStaggerForFit)
			const vec = slideVector(animationType)
			const cfg = {
				targets: characters,
				opacity: [0, 1],
				translateX: vec.dx !== 0 ? [slideStart(vec.dx), '0em'] : undefined,
				translateY: vec.dy !== 0 ? [slideStart(vec.dy), '0em'] : undefined,
				delay: wrapDelayForDirection(buildScaledDelayFn(delay, target, totalDuration, scale), animationType),
			}
			if (scaleFromActive) cfg.scale = [scaleFrom, 1]
			if (blurActive) cfg.filter = [blurFilterFrom, 'blur(0px)']
			if (scale) cfg.duration = scale.duration
			else if (perItemDuration !== undefined) cfg.duration = perItemDuration
			timeline.add(cfg)
			break
		}
	}
	if (timeline) {
		// Store the timeline
		animationTimelines.set(target, timeline)
	}
	return timeline
}

// Function to play animation
const playAnimation = (target) => {
	if (isScrubTarget(target)) {
		return false
	}
	if (!animation_lifecycle_controller.isDocumentVisible()) {
		if (target && target._layTextAnimationWaitingForVisibleTab) {
			return false
		}
		if (target) {
			target._layTextAnimationWaitingForVisibleTab = true
		}
		animation_lifecycle_controller.runWhenDocumentVisible(() => {
			if (!target || !target.isConnected) {
				return
			}
			target._layTextAnimationWaitingForVisibleTab = false
			playAnimation(target)
		})
		return false
	}
	// Same split-DOM race as in the grid `textanimations.js` — initObserver
	// fires the in-viewport play synchronously while `prepareTexts` is async.
	// If `.lay-text-line` / `.char` aren't there yet, addClass(initialized)
	// would flip visibility on while the raw text is in the DOM. Bail and
	// let the `texts_are_split_for_page_overlays` retry-listener pick it up.
	if (!isTextAnimationSplitDomReadyForPlay(target)) {
		target._layPendingPlayAfterSplit = true
		debugTa(target, 'playAnimation: SKIPPED — split DOM not ready, will retry after texts_are_split_for_page_overlays')
		return false
	}
	const hadTimeline = !!animationTimelines.get(target)
	taResizeTraceTarget(target, '[overlay] playAnimation()', {
		hadTimelineBefore: hadTimeline,
		inView: target.classList.contains('text-animation-in-view'),
		readyToPlay: target.classList.contains('text-animation-ready-to-play'),
		stack: typeof Error !== 'undefined' ? new Error().stack?.split('\n').slice(1, 5).join(' ← ') : '',
	})
	taSplitLogTarget(target, '[overlay] playAnimation enter', {
		opacityBefore: target.style.opacity,
		hadTimeline,
		inViewClass: target.classList.contains('text-animation-in-view'),
	})
	snapshotVisualState(target, 'A · playAnimation enter')
	const lineMasks = target.querySelectorAll('.lay-text-line-mask')
	const animationType = target.getAttribute('data-animation-type')

	// Apply the from-state synchronously BEFORE the wrapper's opacity goes
	// to 1 and the initialized class flips visibility on. anime.js doesn't
	// write the timeline's tween from-values until its first rAF tick, so
	// without this set there's a one-frame window where the text is
	// visibility:visible at its end-state — the "show → hide → animate in"
	// flicker. Must run BEFORE `target.style.opacity = 1` because the
	// helper also sets `target.style.opacity = '0'` (a leftover from its
	// resetAnimationState use); we override that back to 1 right after.
	applyPreRevealVisualStateToTarget(target)

	if (animationType !== 'fade_whole_text' && animationType !== 'fade_and_slide_whole_text') {
		target.style.opacity = 1
	}
	snapshotVisualState(target, 'B · after style.opacity=1')

	if (
		animationType === 'masked_lines' ||
		animationType === 'masked_words' ||
		animationType === 'masked_characters'
	) {
		setLineTextMaskRevealClip(lineMasks, true)
	}

	// Get or create timeline
	let timeline = animationTimelines.get(target)
	if (!timeline) {
		debugTa(target, 'playAnimation: create timeline')
		timeline = createAnimationTimeline(target)
	} else {
		debugTa(target, 'playAnimation: reuse existing timeline', {
			progress: timeline.progress,
			completed: timeline.completed,
			paused: timeline.paused,
		})
	}
	snapshotVisualState(target, 'C · after createAnimationTimeline')

	// Add initialized class when animation starts
	target.classList.add('lay-text-animation-initialized')
	snapshotVisualState(target, 'D · after addClass(initialized) — overlay guard may still hide this')

	// Play the timeline
	debugTa(target, 'playAnimation: timeline.play()', {
		animationType,
		opacity: target.style.opacity,
	})
	timeline.play()
	removePageOverlayOpeningGuardAfterAnimationStart(target)
	snapshotVisualState(target, 'E · after timeline.play() — anime.js has NOT applied from-state yet')

	// Snapshot one rAF later — by now anime.js's first tick should have
	// applied the from-state (translateY:100% / opacity:0). If the child
	// transform / opacity at this point still shows the natural state, the
	// browser already painted a "fully visible" frame between D and here.
	requestAnimationFrame(() => {
		snapshotVisualState(target, 'F · 1 rAF after play() — anime first-tick should have run')
		requestAnimationFrame(() => {
			snapshotVisualState(target, 'G · 2 rAFs after play()')
		})
	})
	return true
}

// Retry handler for targets whose `playAnimation` was bailed because the
// split DOM wasn't ready yet. Subscribed to fire after the main
// `texts_are_split_for_page_overlays` handler completes.
window.laytheme.on('texts_are_split_for_page_overlays', function () {
	getCurrentTargetsForPageOverlays().forEach((target) => {
		if (isScrubTarget(target)) return
		if (!target || !target._layPendingPlayAfterSplit) return
		if (!isTextAnimationSplitDomReadyForPlay(target)) return
		target._layPendingPlayAfterSplit = false
		if (playAnimation(target)) {
			target.classList.remove('text-animation-never-played')
			target.classList.remove('text-animation-ready-to-play')
			target.classList.add('text-animation-in-view')
		}
	})
})

// Initialize text splitting
var setup = function () {
	// Clean up any existing observers
	if (window.currentPoTaObservers) {
		window.currentPoTaObservers()
	}
	// page overlays :D
	column_selectors = ['.lay-overlay ' + selector]

	// Initialize text splitting for all matching elements
	const targets = Array.from(document.querySelectorAll(column_selectors))
	setCurrentTargetsForPageOverlays(targets)

	if (targets.length > 0) {
		storeOriginalTexts()
	}
}
setup()

export {
	getCurrentTargetsForPageOverlays,
	initObserver,
	createAnimationTimeline,
	animationTimelines,
	typewriterTimeouts,
}
